import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { api } from './api';

import apiClient from '@/repositories/apiClient';
import moment from 'moment';

const uploadProgress = createAction('element/uploadProgress');

export const elementApi = api.injectEndpoints({
  endpoints: (build) => ({
    getElement: build.query({
      query: (id) => ({
        url: `element/${id}`,
      }),
      providesTags: (result, _err, id) => [{ type: 'Elements', id: result?.data?.model?.ID || id }],
      transformResponse: (response, meta, arg) => {
        return {
          ...response,
          data: {
            ...response.data,
            highlight_start: response.data.highlight_start && response.data.highlight_start !== "0001-01-01T00:00:00Z" ? response.data.highlight_start : '',
            highlight_end: response.data.highlight_end && response.data.highlight_end !== "0001-01-01T00:00:00Z" ? response.data.highlight_end : '',
            remove_date: response.data.remove_date && response.data.remove_date !== "0001-01-01T00:00:00Z" ? response.data.remove_date : '',
            groups: response.data.groups.map((group) => group.model.ID),
          }
        }
      },
    }),
    updateElement: build.mutation({
      query: ({id, item, replaced_file = null, replaced_resolution}) => ({
        url: `element/${id}`,
        method: 'PUT',
        body: JSON.stringify({item: {
          ...item,
          highlight_start: !item.highlight_start || item.highlight_start === "" ? "0001-01-01T00:00:00Z" : moment(item.highlight_start),
          highlight_end: !item.highlight_end || item.highlight_end === "" ? "0001-01-01T00:00:00Z" : moment(item.highlight_end),
          remove_date: !item.remove_date || item.remove_date === "" ? "0001-01-01T00:00:00Z" : moment(item.remove_date),
          groups: item.groups.map(group => ({model: {ID: group}}))}, replaced_resolution, replaced_file, replaced: replaced_file !== null
        }),
      }),
      // Update affects three different caches: the concrete element row,
      // the gallery LIST and both dashboard panels (NEWEST + HIGHLIGHTED).
      // Without the NEWEST/HIGHLIGHTED hits the dashboard kept showing the
      // pre-change state until a hard reload.
      invalidatesTags: (result) => {
        const base = [
          { type: 'Elements', id: 'LIST' },
          { type: 'Elements', id: 'NEWEST' },
          { type: 'Elements', id: 'HIGHLIGHTED' },
        ];
        return result?.data?.model?.ID
          ? [{ type: 'Elements', id: result.data.model.ID }, ...base]
          : base;
      },
    }),
    deleteElement: build.mutation({
      // Accepts either a plain id (legacy callers) or { id, force, cascade }.
      // Without force the backend may answer 409 with a list of streams
      // still referencing the element — callers then re-issue with
      // force:true after confirming with the user.
      query: (arg) => {
        const id = typeof arg === 'object' ? arg.id : arg;
        const force = typeof arg === 'object' ? !!arg.force : false;
        const cascade = typeof arg === 'object' ? arg.cascade : null;
        const params = new URLSearchParams();
        if (force) params.set('force', 'true');
        if (cascade) params.set('cascade', cascade);
        const query = params.toString();
        return {
          url: `element/${id}${query ? `?${query}` : ''}`,
          method: 'DELETE',
        };
      },
      // Force-delete also detaches stream items, so the Streams list
      // needs to be re-fetched too — without that invalidation the
      // dashboard keeps showing stale stream cards until the next manual
      // refresh.
      invalidatesTags: (result, error) => error ? [] : [
        { type: 'Elements', id: 'LIST' },
        { type: 'Elements', id: 'NEWEST' },
        { type: 'Elements', id: 'HIGHLIGHTED' },
        { type: 'Streams', id: 'LIST' },
      ],
    }),
    createElement: build.mutation({
      query: ({item, file, resolution, rotate}) => ({
        url: 'element',
        method: 'POST',
        body: JSON.stringify({
          form: {
            ...item,
            highlight_start: !item.highlight_start || item.highlight_start === "" ? "0001-01-01T00:00:00Z" : item.highlight_start,
            highlight_end: !item.highlight_end || item.highlight_end === "" ? "0001-01-01T00:00:00Z" : item.highlight_end,
            remove_date: !item.remove_date || item.remove_date === "" ? "0001-01-01T00:00:00Z" : item.remove_date,
          },
          file, resolution, rotate
        }),
      }),
      // Fresh upload has to flip the dashboard's "Neuste Elemente" panel.
      // The previous LIST-only invalidation left NEWEST cached, so the
      // panel kept showing the pre-upload tiles.
      invalidatesTags: (result, error) => error ? [] : [
        { type: 'Elements', id: 'LIST' },
        { type: 'Elements', id: 'NEWEST' },
        { type: 'Elements', id: 'HIGHLIGHTED' },
      ],
    }),
  }),
})


const uploadElement = createAsyncThunk(
  'element/upload',
  async (file, {dispatch}) => {

    let data = new FormData();
    data.append('file', file, file.name);

    const requestOptions = {
      method: 'POST',
      data: data,
      timeout: 180000 * 2,
      url: `element/upload`,
      onUploadProgress: (p) => {
        let progress = (p.loaded * 100.0) / p.total;
        dispatch(uploadProgress(progress))
      },
    };

    // Let the thrown error propagate — createAsyncThunk turns it into a
    // rejected action with `action.error` set (SerializedError). We
    // deliberately don't use rejectWithValue here because the global
    // ErrorFallbackMiddleware would then fire a generic toast on top of
    // the specific one ElementForm shows in its own .catch handler.
    const response = await apiClient.request(requestOptions);
    return { data: response.data };
  }
)

export const elementSlice = createSlice({
  name: "element",
  initialState: {
    is_uploading: false,
    upload_progress: 0,
    status: {
      removed: [],
      added: [],
      changed: [],
    }
  },
  reducers: {
    uploadProgress: (state, action) => {
      state.upload_progress = action.payload;
    },
    informAddedElements: (state, action) => {
      state.status.added = action.payload;
    },
    informRemovedElements: (state, action) => {
      state.status.removed = action.payload;
    },
    informChangedElements: (state, action) => {
      state.status.changed = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Reducers must stay pure — Redux 5 throws "may not dispatch actions"
    // (error #3) if we kick off a toast from here. Toasts for the upload
    // outcome live in the caller (ElementForm.js) instead, where we already
    // toast the surrounding create/update result anyway.
    builder.addCase(uploadElement.pending, (state, _) => {
      state.is_uploading = true;
      state.upload_progress = 0;
    })

    builder.addCase(uploadElement.fulfilled, (state, _action) => {
      state.upload_progress = 0;
      state.is_uploading = false;
    })

    builder.addCase(uploadElement.rejected, (state, _action) => {
      state.upload_progress = 0;
      state.is_uploading = false;
    });
  }
});

const { actions, reducer } = elementSlice;

export const informAboutAddedElements = (ids = []) => {
  return actions.informAddedElements(ids);
}

export const informAboutRemovedElements = (ids = []) => {
  return actions.informRemovedElements(ids);
}

export const informAboutChangedElements = (ids = []) => {
  return actions.informChangedElements(ids);
}

export const setAsViewed = async (ids=[]) => {
  return await apiClient.post("/element/viewed", JSON.stringify({ ids: ids }))
}

export default reducer;

export {uploadElement};
export const {
  useGetElementQuery,
  useLazyGetElementQuery,
  useCreateElementMutation,
  useUpdateElementMutation,
  useDeleteElementMutation,
} = elementApi;
